/**
 * Prevent Razorpay Magic Checkout from intercepting checkout buttons
 * on international markets (UAE, Saudi Arabia, UK, Singapore, Malaysia, USA, Europe, Canada, Australia)
 * 
 * This script should only run on international markets, not on India (default)
 * India uses Razorpay Magic Checkout, so it should work normally there.
 */
(function() {
  'use strict';
  
  /**
   * Detect if this is an international market (not India)
   * @returns {boolean}
   */
  function isInternationalMarket() {
    // Check if MarketConfig is available (preferred method)
    if (window.MarketConfig) {
      const marketInfo = window.MarketConfig.detectMarket();
      // If market is not 'default' (India), it's an international market
      return marketInfo.key !== 'default';
    }
    
    // Fallback: Check pathname for international market paths
    const pathname = window.location.pathname;
    const hostname = window.location.hostname;
    
    // Market subfolders are always {language}-{market}, so match the market
    // suffix rather than listing every language-market pair.
    const INTERNATIONAL_MARKET = /(?:^|\/|\.)[a-z]{2}-(?:ae|sa|gb|sg|us|eu|ca|au|my)(?:\/|$|\.)/;

    // Check pathname
    if (INTERNATIONAL_MARKET.test(pathname)) {
      return true;
    }

    // Check hostname
    {
      if (INTERNATIONAL_MARKET.test(hostname)) {
        return true;
      }
    }
    
    // Check window.shopifyLocale
    if (window.shopifyLocale && window.shopifyLocale.code) {
      const locale = window.shopifyLocale.code;
      if (locale !== 'en' && (locale.startsWith('en-') || locale.startsWith('ar-'))) {
        return true;
      }
    }
    
    // Default: not an international market (India)
    return false;
  }
  
  // Exit if not an international market
  if (!isInternationalMarket()) {
    return; // Let Razorpay Magic Checkout work normally for India
  }
  
  /**
   * Prevent Magic Checkout from intercepting checkout buttons
   */
  function preventMagicCheckout() {
    // Wait for checkout initialization script to load
    // This could be tally-checkout.js, custom-checkout.js, or modular-checkout.js
    const checkoutScripts = [
      window.initTallyCheckout,
      window.initCustomCheckout,
      window.initModularCheckout
    ];
    
    const hasCheckoutScript = checkoutScripts.some(script => 
      script && typeof script === 'function'
    );
    
    if (!hasCheckoutScript) {
      setTimeout(preventMagicCheckout, 100);
      return;
    }
    
    // Find all checkout buttons that might be intercepted by Razorpay
    // These could be: data-tally-checkout-button, data-custom-checkout-button, etc.
    const checkoutButtonSelectors = [
      '[data-tally-checkout-button]',
      '[data-custom-checkout-button]',
      '[data-modular-checkout-button]',
      'button[type="submit"][form*="checkout"]',
      '.checkout-submit',
      '#checkout-submit',
      '#checkout-submit-mobile'
    ];
    
    let clonedAny = false;
    
    checkoutButtonSelectors.forEach(selector => {
      const buttons = document.querySelectorAll(selector);
      
      buttons.forEach(function(button) {
        // Skip if button already processed
        if (button.dataset.magicCheckoutPrevented === 'true') {
          return;
        }
        
        // Skip if button already has handler attached
        if (button.dataset.checkoutHandlerAttached === 'true') {
          return;
        }
        
        // Clone button to remove Razorpay Magic Checkout event listeners
        const newButton = button.cloneNode(true);
        
        // Preserve all data attributes
        Array.from(button.attributes).forEach(attr => {
          if (attr.name.startsWith('data-')) {
            newButton.setAttribute(attr.name, attr.value);
          }
        });
        
        // Mark as prevented
        newButton.setAttribute('data-magic-checkout-prevented', 'true');
        
        // Replace button
        if (button.parentNode) {
          button.parentNode.replaceChild(newButton, button);
          clonedAny = true;
        }
      });
    });
    
    // If we cloned any buttons, trigger re-initialization of checkout scripts
    if (clonedAny) {
      setTimeout(function() {
        // Try to re-initialize checkout scripts
        if (window.initTallyCheckout && typeof window.initTallyCheckout === 'function') {
          window.initTallyCheckout();
        }
        if (window.initCustomCheckout && typeof window.initCustomCheckout === 'function') {
          window.initCustomCheckout();
        }
        if (window.initModularCheckout && typeof window.initModularCheckout === 'function') {
          window.initModularCheckout();
        }
      }, 10);
    }
  }
  
  /**
   * Wait for checkout script to load before running prevention
   */
  function waitForCheckoutScriptThenRun() {
    const checkoutScripts = [
      window.initTallyCheckout,
      window.initCustomCheckout,
      window.initModularCheckout
    ];
    
    const hasCheckoutScript = checkoutScripts.some(script => 
      script && typeof script === 'function'
    );
    
    if (hasCheckoutScript) {
      // Run after a delay to let event delegation set up
      setTimeout(function() {
        preventMagicCheckout();
        // Run again after a short delay to catch any late-loading buttons
        setTimeout(preventMagicCheckout, 100);
      }, 200);
    } else {
      setTimeout(waitForCheckoutScriptThenRun, 50);
    }
  }
  
  // Start waiting for script to load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      waitForCheckoutScriptThenRun();
    });
  } else {
    waitForCheckoutScriptThenRun();
  }
  
  // Also run on dynamic content (e.g., cart drawer updates)
  // Optimized: Only observe specific containers instead of entire document.body
  const observer = new MutationObserver(function(mutations) {
    let shouldRun = false;
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            const hasCheckoutButton = node.querySelector && (
              node.querySelector('[data-tally-checkout-button]') ||
              node.querySelector('[data-custom-checkout-button]') ||
              node.querySelector('[data-modular-checkout-button]')
            );
            if (hasCheckoutButton || (node.matches && (
              node.matches('[data-tally-checkout-button]') ||
              node.matches('[data-custom-checkout-button]') ||
              node.matches('[data-modular-checkout-button]')
            ))) {
              shouldRun = true;
            }
          }
        });
      }
    });
    
    if (shouldRun) {
      setTimeout(preventMagicCheckout, 100);
    }
  });
  
  // Optimized: Only observe specific containers (cart drawer, cart bar) instead of entire body
  function setupObserver() {
    const containersToWatch = [
      document.querySelector('.cart-drawer'),
      document.querySelector('.cart-bar'),
      document.querySelector('[data-cart-drawer]'),
      document.querySelector('[data-cart-bar]')
    ].filter(Boolean); // Remove null values
    
    // If no specific containers found, fall back to body but with limited scope
    if (containersToWatch.length === 0) {
      if (document.body) {
        observer.observe(document.body, {
          childList: true,
          subtree: false // Only direct children, not all descendants
        });
      }
    } else {
      // Observe only the specific containers
      containersToWatch.forEach(function(container) {
        observer.observe(container, {
          childList: true,
          subtree: true
        });
      });
    }
  }
  
  if (document.body) {
    setupObserver();
  } else {
    document.addEventListener('DOMContentLoaded', setupObserver);
  }
})();

